import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, ChevronDown } from "lucide-react";
import axios from "axios";
import ShowroomCard from "../components/ShowroomCard";
import { useLanguage } from "../contexts/LanguageContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

// Fixed bento pattern: every 6 cards follow this span pattern
// Row A: [2, 1]  → col-span-2, col-span-1
// Row B: [1, 1, 1] → three equal
// This repeats, avoiding clustering of same-size items
const SPAN_PATTERN = [2, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1];

export default function Home() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const loadMoreRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -80]);
  const heroOpacity = useTransform(scrollY, [0, 350], [1, 0]);

  const categories = ["all", "sculpture", "geometric", "architecture", "functional"];

  const fetchProducts = useCallback(async (pageNum = 1, cat = "all", reset = false) => {
    if (loading && !reset) return;
    setLoading(true);
    try {
      const params = { page: pageNum, limit: 6, in_showroom: true, ...(cat !== "all" && { category: cat }) };
      const { data } = await axios.get(`${API}/products`, { params });
      const newProds = data.products || [];
      setProducts(prev => reset ? newProds : [...prev, ...newProds]);
      const loaded = reset ? newProds.length : products.length + newProds.length;
      setHasMore(newProds.length === 6 && loaded < data.total);
      setPage(pageNum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [loading, products.length]);

  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    fetchProducts(1, activeCategory, true);
  }, [activeCategory]); // eslint-disable-line

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchProducts(page + 1, activeCategory);
        }
      },
      { threshold: 0.1, rootMargin: "200px" }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page, activeCategory]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden" ref={heroRef}>
        {/* BG Parallax */}
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url(https://images.unsplash.com/photo-1771924310799-930349452c76?w=1920&q=80)" }}
          />
          <div className="absolute inset-0 bg-background/75 dark:bg-background/85" />
        </motion.div>

        {/* Hero content – centered */}
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-40 flex flex-col items-center text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-[11px] font-mono tracking-[0.35em] uppercase text-foreground/40 mb-6 block"
          >
            {t("home.hero.subtitle")}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold tracking-tighter text-foreground leading-[1.05] mb-8 whitespace-pre-line text-center"
          >
            {t("home.hero.title")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-base text-foreground/60 max-w-md leading-relaxed mb-12 text-center"
          >
            {t("home.hero.desc")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="#showroom"
              data-testid="hero-cta-btn"
              className="inline-flex items-center gap-3 px-8 py-4 bg-foreground text-background text-sm font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all duration-300"
            >
              {t("home.hero.cta")}
              <ChevronDown size={14} strokeWidth={1.5} />
            </a>
            <button
              onClick={() => navigate("/shop")}
              data-testid="hero-shop-btn"
              className="inline-flex items-center gap-3 px-8 py-4 border border-border text-foreground/60 text-sm font-mono tracking-widest uppercase rounded-sm hover:border-foreground/40 hover:text-foreground transition-all duration-300"
            >
              {t("home.hero.shop")}
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator – text + animated dot */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="text-[9px] font-mono tracking-[0.4em] uppercase text-foreground/25 select-none">
            scroll
          </span>
          <motion.div
            animate={{ y: [0, 7, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="w-1 h-1 rounded-full bg-foreground/30"
          />
        </motion.div>
      </section>

      {/* ── Showroom ──────────────────────────────────────── */}
      <section id="showroom" className="max-w-7xl mx-auto px-6 py-24">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <span className="text-[11px] font-mono tracking-[0.35em] uppercase text-foreground/30 mb-3 block">
            {t("home.showroom.subtitle")}
          </span>
          <h2 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-foreground">
            {t("home.showroom.title")}
          </h2>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-2 mb-10"
        >
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`filter-${cat}`}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-[11px] font-mono tracking-widest uppercase rounded-sm transition-all duration-300 ${
                activeCategory === cat
                  ? "bg-foreground text-background"
                  : "border border-border text-foreground/40 hover:border-foreground/30 hover:text-foreground/80"
              }`}
            >
              {t(`home.categories.${cat}`)}
            </button>
          ))}
        </motion.div>

        {/* Bento Grid – fixed pattern, no price */}
        <div className="showroom-grid">
          {products.map((product, index) => {
            const span = SPAN_PATTERN[index % SPAN_PATTERN.length];
            return (
              <div key={product.id} className={span === 2 ? "span-2" : ""}>
                <ShowroomCard product={product} index={index} span={span} />
              </div>
            );
          })}
        </div>

        {/* Infinite scroll sentinel + status */}
        <div ref={loadMoreRef} className="flex justify-center py-14">
          {loading && (
            <div className="flex items-center gap-3 text-foreground/30">
              <div className="w-3 h-3 border border-foreground/20 border-t-foreground/50 rounded-full animate-spin" />
              <span className="text-[11px] font-mono tracking-widest uppercase">{t("home.loading")}</span>
            </div>
          )}
          {!hasMore && products.length > 0 && !loading && (
            <p className="text-[11px] font-mono tracking-[0.3em] text-foreground/20 uppercase">{t("home.no_more")}</p>
          )}
        </div>
      </section>
    </div>
  );
}
